import input from "@krystal/css/components/input.css?inline";
import radioGroup from "@krystal/css/components/radio-group.css?inline";
import radio from "@krystal/css/components/radio.css?inline";
import { html, unsafeCSS } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import type { UploadFileItem } from "@/components/upload";
import type Upload from "@/components/upload/upload";
import { cn } from "@/utilities/css";
import "@/components/alert";
import "@/components/button";
import "@/components/featured-icon";
import "@/components/input";
import "@/components/modal";
import "@/components/select";
import "@/components/textarea";
import "@/components/upload";
import EkoElement from "@/utilities/element";
import style from "./feedback-widget.css?inline";

export type FeedbackWidgetValue = "bug" | "feedback";

export type FeedbackWidgetSubmitDetail = {
  feedback: string;
  feedbackType: string;
  screenshots?: UploadFileItem[];
  title?: string;
};

interface Option {
  description: string;
  icon: string;
  label: string;
  value: FeedbackWidgetValue;
}

interface FeedbackSubtype {
  label: string;
  placeholder: string;
  textLabel: string;
  value: string;
}

const OPTIONS: Option[] = [
  {
    description: "Something's not working right",
    icon: "alert-triangle",
    label: "Report a bug",
    value: "bug",
  },
  {
    description: "Suggestions and comments",
    icon: "message-text-square-01",
    label: "Provide feedback",
    value: "feedback",
  },
];

const FEEDBACK_SUBTYPES: FeedbackSubtype[] = [
  {
    label: "Suggestion",
    placeholder: "Write your suggestion...",
    textLabel: "Your suggestion",
    value: "suggestion",
  },
  {
    label: "Compliment",
    placeholder: "Write your compliment...",
    textLabel: "Your compliment",
    value: "compliment",
  },
  {
    label: "General",
    placeholder: "Write your feedback...",
    textLabel: "Your feedback",
    value: "general",
  },
];

@customElement("eko-feedback-widget")
class FeedbackWidget extends EkoElement {
  static styles = [
    ...EkoElement.styles,
    unsafeCSS(input),
    unsafeCSS(radio),
    unsafeCSS(radioGroup),
    unsafeCSS(style),
  ];

  @state() private _bugDetails = "";

  @state() private _bugTitle = "";

  @state() private _errorMessage: string | undefined = undefined;

  @state() private _feedbackText = "";

  @state() private _feedbackType = "suggestion";

  @state() private _loading = false;

  private _onBack = () => {
    this._errorMessage = undefined;
    this._step = 1;
  };

  private _onBugDetailsInput = (e: CustomEvent<{ value: string }>) => {
    if (typeof e.detail?.value === "string") this._bugDetails = e.detail.value;
  };

  private _onBugTitleInput = (e: CustomEvent<{ value: string }>) => {
    if (typeof e.detail?.value === "string") this._bugTitle = e.detail.value;
  };

  private _onCancel = (event: Event) => {
    event.preventDefault();
    this.open = false;
    this.dispatchEvent(new Event("cancel", { bubbles: true, composed: true }));
  };

  private _onFeedbackInput = (e: CustomEvent<{ value: string }>) => {
    if (typeof e.detail?.value === "string") this._feedbackText = e.detail.value;
  };

  private _onFeedbackTypeChange = (e: CustomEvent<{ value: string }>) => {
    this._feedbackType = e.detail.value;
  };

  private _onNext = () => {
    if (!this._value) return;
    this._step = 2;
  };

  private _onOptionKeydown = (e: KeyboardEvent, value: FeedbackWidgetValue) => {
    const options = Array.from(
      this.shadowRoot!.querySelectorAll<HTMLElement>(".eko-feedback-widget__option")
    );
    const index = OPTIONS.findIndex((opt) => opt.value === value);

    switch (e.key) {
      case "ArrowDown":
      case "ArrowRight": {
        e.preventDefault();
        const nextIndex = (index + 1) % options.length;
        this._select(OPTIONS[nextIndex].value);
        this.updateComplete.then(() => options[nextIndex].focus());
        break;
      }
      case "ArrowUp":
      case "ArrowLeft": {
        e.preventDefault();
        const prevIndex = (index - 1 + options.length) % options.length;
        this._select(OPTIONS[prevIndex].value);
        this.updateComplete.then(() => options[prevIndex].focus());
        break;
      }
      case " ":
      case "Enter":
        e.preventDefault();
        this._select(value);
        break;
    }
  };

  private _onSubmit = () => {
    this._loading = true;
    this._errorMessage = undefined;
    const isBug = this._value === "bug";
    this.dispatchEvent(
      new CustomEvent<FeedbackWidgetSubmitDetail>("submit", {
        bubbles: true,
        composed: true,
        detail: {
          feedback: isBug ? this._bugDetails : this._feedbackText,
          feedbackType: isBug ? "bug" : this._feedbackType,
          screenshots: isBug
            ? (this._uploadElement?.files ?? []).filter((f) => f.status !== "error")
            : undefined,
          title: isBug ? this._bugTitle : undefined,
        },
      })
    );
  };

  @state() private _step: 1 | 2 | 3 = 1;

  @query("eko-upload") private _uploadElement?: Upload | null;

  @state() private _value: FeedbackWidgetValue | undefined = undefined;

  @property() heading = "We would love to hear your feedback!";

  @property() icon = "message-circle-01";

  @property({ reflect: true, type: Boolean }) open = false;

  @property({ attribute: "supporting-text" }) supportingText =
    "What type of feedback you have for us?";

  private _renderError() {
    if (!this._errorMessage) return html``;
    return html`<eko-alert variant="danger">${this._errorMessage}</eko-alert>`;
  }

  private _renderOption(opt: Option) {
    const checked = this._value === opt.value;
    const tabindex = checked || (!this._value && opt.value === "bug") ? 0 : -1;

    return html`
      <div
        class=${cn("eko-feedback-widget__option", {
          "eko-feedback-widget__option--checked": checked,
        })}
        role="radio"
        aria-checked=${String(checked)}
        tabindex=${tabindex}
        @click=${() => this._select(opt.value)}
        @keydown=${(e: KeyboardEvent) => this._onOptionKeydown(e, opt.value)}>
        <div class="eko-feedback-widget__option-content">
          <eko-featured-icon
            size="sm"
            variant="square-light"
            theme="accent"
            icon=${opt.icon}></eko-featured-icon>
          <div class="eko-feedback-widget__option-text">
            <span class="eko-feedback-widget__option-label">${opt.label}</span>
            <span class="eko-feedback-widget__option-description">${opt.description}</span>
          </div>
        </div>
        <span class="eko-feedback-widget__option-radio" aria-hidden="true"></span>
      </div>
    `;
  }

  private _renderStep1() {
    return html`
      <eko-featured-icon
        slot="icon"
        variant="square-modern"
        theme="neutral"
        icon=${this.icon}></eko-featured-icon>
      <span slot="title" class="eko-feedback-widget__title">${this.heading}</span>
      <span slot="subtitle">${this.supportingText}</span>

      <div class="eko-feedback-widget__options" role="radiogroup" aria-label=${this.heading}>
        ${OPTIONS.map((opt) => this._renderOption(opt))}
      </div>

      <div slot="footer" class="eko-feedback-widget__footer">
        <eko-button @click=${this._onCancel}>Cancel</eko-button>
        <eko-button variant="primary" ?disabled=${!this._value} @click=${this._onNext}>
          Next
        </eko-button>
      </div>
    `;
  }

  private _renderStep2() {
    return this._value === "bug" ? this._renderStep2Bug() : this._renderStep2Feedback();
  }

  private _renderStep2Bug() {
    return html`
      <eko-featured-icon
        slot="icon"
        variant="square-modern"
        theme="neutral"
        icon="alert-triangle"></eko-featured-icon>
      <span slot="title" class="eko-feedback-widget__title">Tell us what went wrong</span>

      <div class="eko-feedback-widget__form">
        ${this._renderError()}
        <eko-input
          label="Title"
          placeholder="Short summary"
          .value=${this._bugTitle}
          ?required=${true}
          @input=${this._onBugTitleInput}></eko-input>
        <eko-textarea
          label="Details"
          placeholder="Tell us about what happened..."
          .value=${this._bugDetails}
          .rows=${5}
          @input=${this._onBugDetailsInput}></eko-textarea>
        <div class="eko-feedback-widget__screenshots">
          <span class="eko-feedback-widget__screenshots-label">Screenshots</span>
          <eko-upload multiple accept="image/*" max-files="3" .maxFileSize=${5 * 1024 * 1024}>
            <input type="file" accept="image/*" multiple />
            <span slot="hint">Attach up to 3 screenshots (max 5 MB each)</span>
          </eko-upload>
        </div>
      </div>

      <div slot="footer" class="eko-feedback-widget__footer">
        <eko-button ?disabled=${this._loading} @click=${this._onBack}>Back</eko-button>
        <eko-button
          variant="primary"
          ?disabled=${!this._bugTitle || this._loading}
          ?loading=${this._loading}
          @click=${this._onSubmit}
          >Submit</eko-button
        >
      </div>
    `;
  }

  private _renderStep2Feedback() {
    const subtype = this._currentSubtype;
    const step1Option = OPTIONS.find((o) => o.value === this._value);

    return html`
      <eko-featured-icon
        slot="icon"
        variant="square-modern"
        theme="neutral"
        icon=${step1Option?.icon ?? this.icon}></eko-featured-icon>
      <span slot="title" class="eko-feedback-widget__title">Let us know your feedback</span>

      <div class="eko-feedback-widget__form">
        ${this._renderError()}
        <eko-select label="Type of feedback" @change=${this._onFeedbackTypeChange}>
          ${FEEDBACK_SUBTYPES.map(
            (s) =>
              html`<option value=${s.value} ?selected=${this._feedbackType === s.value}>
                ${s.label}
              </option>`
          )}
        </eko-select>
        <eko-textarea
          label=${subtype.textLabel}
          placeholder=${subtype.placeholder}
          .value=${this._feedbackText}
          .rows=${5}
          ?required=${true}
          @input=${this._onFeedbackInput}></eko-textarea>
      </div>

      <div slot="footer" class="eko-feedback-widget__footer">
        <eko-button ?disabled=${this._loading} @click=${this._onBack}>Back</eko-button>
        <eko-button
          variant="primary"
          ?disabled=${!this._feedbackText || this._loading}
          ?loading=${this._loading}
          @click=${this._onSubmit}
          >Submit</eko-button
        >
      </div>
    `;
  }

  private _renderStep3() {
    return html`
      <eko-featured-icon
        slot="icon"
        variant="square-modern"
        theme="success"
        icon="check-circle"></eko-featured-icon>
      <span slot="title" class="eko-feedback-widget__title">Thank you for your feedback!</span>
      <span slot="subtitle">Your feedback has been received and will be reviewed by our team.</span>

      <div slot="footer" class="eko-feedback-widget__footer">
        <eko-button variant="primary" @click=${this._onCancel}>Close</eko-button>
      </div>
    `;
  }

  private _select(value: FeedbackWidgetValue) {
    this._value = value;
  }

  render() {
    return html`
      <eko-modal
        layout=${this._step === 2 ? "default" : "centered"}
        ?open=${this.open}
        @cancel=${this._onCancel}>
        ${this._step === 1
          ? this._renderStep1()
          : this._step === 2
            ? this._renderStep2()
            : this._renderStep3()}
      </eko-modal>
    `;
  }

  showError(message = "Something went wrong. Please try again.") {
    this._loading = false;
    this._errorMessage = message;
  }

  showSuccess() {
    this._loading = false;
    this._step = 3;
  }

  updated(changedProperties: Map<PropertyKey, unknown>) {
    if (changedProperties.has("_step") && this._step === 2) {
      this.updateComplete.then(() => {
        const selector = this._value === "bug" ? "eko-input" : "eko-textarea";
        this.shadowRoot?.querySelector<HTMLElement>(selector)?.focus();
      });
    }

    if (changedProperties.has("open") && !this.open) {
      this._loading = false;
      this._bugDetails = "";
      this._bugTitle = "";
      this._errorMessage = undefined;
      this._feedbackText = "";
      this._feedbackType = "suggestion";
      this._step = 1;
      this._value = undefined;
    }
  }

  private get _currentSubtype(): FeedbackSubtype {
    return FEEDBACK_SUBTYPES.find((s) => s.value === this._feedbackType) ?? FEEDBACK_SUBTYPES[0];
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "eko-feedback-widget": FeedbackWidget;
  }
}

export default FeedbackWidget;
